import { useEffect, useLayoutEffect } from "react";

const REVEAL_SELECTOR = [
  ".block > .eyebrow",
  ".block > .drow",
  ".block > .section-intro",
  ".block > .dcard",
  ".stats > *",
  ".ai-principles",
  ".ai-grid > *",
  ".log-item",
  ".case > *",
  ".decisions > *",
  ".case-results > *",
  ".about-intro",
  ".principle",
  ".about-grid > *",
  ".about-quote",
  ".foot-cta > *",
  ".foot-top > .cf",
  ".foot-col",
].join(",");

const TILT_SELECTOR = ".stat, .ai-card, .log-item .dcard, #education .dcard";
const MAGNET_SELECTOR = ".btn-primary, .btn-ghost";
const GLYPHS = "abcdefghijklmnopqrstuvwxyz0123456789#%&*+=<>/";

const SECTION_LABELS = {
  impact: "impact",
  ai: "ai-in-production",
  career: "career",
  work: "case-studies",
  about: "beyond-the-code",
  education: "education",
};

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function countUp(el) {
  const m = el.textContent.match(/^(\d+(?:\.\d+)?)(.*)$/);
  if (!m) return;
  const target = parseFloat(m[1]);
  const decimals = (m[1].split(".")[1] || "").length;
  const suffix = m[2];
  const start = performance.now();
  const dur = 1300;
  const tick = (now) => {
    const t = Math.min((now - start) / dur, 1);
    const eased = 1 - Math.pow(1 - t, 4);
    el.textContent = (target * eased).toFixed(decimals) + suffix;
    if (t < 1) requestAnimationFrame(tick);
    else el.textContent = m[0];
  };
  requestAnimationFrame(tick);
}

// Decode text from random glyphs into the real string, left to right.
function scramble(el, duration = 700) {
  const final = el.dataset.text || el.textContent;
  el.dataset.text = final;
  const start = performance.now();
  const tick = (now) => {
    const t = Math.min((now - start) / duration, 1);
    const settled = Math.floor(t * final.length);
    let out = final.slice(0, settled);
    for (let i = settled; i < final.length; i++) {
      out += final[i] === " " ? " " : GLYPHS[(Math.random() * GLYPHS.length) | 0];
    }
    el.textContent = out;
    if (t < 1) requestAnimationFrame(tick);
    else el.textContent = final;
  };
  requestAnimationFrame(tick);
}

// Scroll reveal with stagger and blur, count-up numbers, text decode,
// card spotlight and tilt, magnetic buttons, click ripples, a scroll-filled
// career timeline, and a section crumb.
export default function useMotion() {
  useLayoutEffect(() => {
    if (prefersReduced() || !("IntersectionObserver" in window)) return undefined;

    const nodes = Array.from(document.querySelectorAll(REVEAL_SELECTOR));
    const seen = new Set();
    const counters = new Set();

    nodes.forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      const siblings = Array.from(el.parentElement.children).filter((c) =>
        nodes.includes(c)
      );
      const i = Math.min(siblings.indexOf(el), 6);
      el.style.setProperty("--d", `${i * 80}ms`);
      el.classList.add("reveal");
      el.querySelectorAll(".stat-value").forEach((v) => {
        if (/^\d/.test(v.textContent)) counters.add(v);
      });
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          io.unobserve(el);
          el.classList.add("in");
          el.querySelectorAll(".stat-value").forEach((v) => {
            if (counters.has(v)) countUp(v);
          });
          const eyebrow = el.matches(".eyebrow")
            ? el
            : el.querySelector(":scope > .eyebrow");
          if (eyebrow) setTimeout(() => scramble(eyebrow, 600), 120);
          setTimeout(() => el.classList.remove("reveal", "in"), 1700);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
    );
    seen.forEach((el) => io.observe(el));

    document.querySelectorAll("[data-scramble]").forEach((el) => {
      setTimeout(() => scramble(el, 900), 150);
    });

    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const reduced = prefersReduced();
    const root = document.documentElement;

    // Click ripples on buttons.
    const onClick = (e) => {
      const target = e.target.closest(".btn-primary, .btn-ghost, .dev-ghost");
      if (!target || reduced) return;
      const rect = target.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 2;
      const ripple = document.createElement("span");
      ripple.className = "ripple";
      ripple.style.width = ripple.style.height = `${size}px`;
      ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
      ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
      target.appendChild(ripple);
      setTimeout(() => ripple.remove(), 650);
    };

    // Card spotlight, card tilt, and magnetic buttons.
    let tilted = null;
    let magnet = null;
    const resetTilt = (el) => {
      el.style.setProperty("--rx", "0deg");
      el.style.setProperty("--ry", "0deg");
    };
    const resetMagnet = (el) => {
      el.style.setProperty("--tx", "0px");
      el.style.setProperty("--ty", "0px");
    };
    const onPointerMove = (e) => {
      if (reduced || e.pointerType === "touch") return;
      const card = e.target.closest(".dcard");
      if (card) {
        const r = card.getBoundingClientRect();
        card.style.setProperty("--mx", `${e.clientX - r.left}px`);
        card.style.setProperty("--my", `${e.clientY - r.top}px`);
      }

      const tilt = e.target.closest(TILT_SELECTOR);
      if (tilted && tilted !== tilt) resetTilt(tilted);
      tilted = tilt;
      if (tilt) {
        const r = tilt.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        tilt.style.setProperty("--rx", `${(-py * 6).toFixed(2)}deg`);
        tilt.style.setProperty("--ry", `${(px * 8).toFixed(2)}deg`);
      }

      const btn = e.target.closest(MAGNET_SELECTOR);
      if (magnet && magnet !== btn) resetMagnet(magnet);
      magnet = btn;
      if (btn) {
        const r = btn.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        btn.style.setProperty("--tx", `${(dx * 0.18).toFixed(1)}px`);
        btn.style.setProperty("--ty", `${(dy * 0.28).toFixed(1)}px`);
      }
    };

    // The career timeline fill eases toward the scroll position.
    let raf = 0;
    let logCurrent = 0;
    let logTarget = 0;
    const log = document.querySelector(".log");
    const step = () => {
      logCurrent += (logTarget - logCurrent) * 0.12;
      if (Math.abs(logTarget - logCurrent) < 0.0005) {
        logCurrent = logTarget;
        raf = 0;
      } else {
        raf = requestAnimationFrame(step);
      }
      if (log) log.style.setProperty("--fill", logCurrent.toFixed(4));
    };
    const onScroll = () => {
      if (log) {
        const r = log.getBoundingClientRect();
        const vh = window.innerHeight;
        logTarget = Math.min(Math.max((vh * 0.75 - r.top) / r.height, 0), 1);
      }
      if (reduced) {
        logCurrent = logTarget;
        step();
      } else if (!raf) {
        raf = requestAnimationFrame(step);
      }
    };

    // Section crumb in the top bar.
    const crumb = document.querySelector(".crumb");
    let currentSection = "";
    const setCrumb = (id) => {
      if (!crumb || id === currentSection) return;
      currentSection = id;
      crumb.classList.remove("swap");
      void crumb.offsetWidth; // restart the animation
      crumb.textContent = id ? ` / ${SECTION_LABELS[id]}` : "";
      crumb.classList.add("swap");
    };
    const sectionObserver =
      "IntersectionObserver" in window
        ? new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) setCrumb(entry.target.id);
              });
            },
            { rootMargin: "-45% 0px -50% 0px" }
          )
        : null;
    if (sectionObserver) {
      Object.keys(SECTION_LABELS).forEach((id) => {
        const el = document.getElementById(id);
        if (el) sectionObserver.observe(el);
      });
    }
    const onTopCheck = () => {
      if (root.scrollTop < 200) setCrumb("");
    };

    document.addEventListener("click", onClick);
    document.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scroll", onTopCheck, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", onTopCheck);
      window.removeEventListener("resize", onScroll);
      if (sectionObserver) sectionObserver.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);
}
